/**
 * All Drawings tab (wave 2):
 *  - #2 a scan-first table: Drawing (title · ID · type · rev · SD no.) | Disc. | Block / Zone | Status
 *    (with why: "sent 23 May · 130 d, no reply", "Blocker: Awaiting drawings") | Responsible | Updated;
 *    the other columns (Project, Floor / Area, Action note, Target date, PDF, DWG, Notes, Project No.)
 *    live in "Columns" (remembered per browser); the header sticks under the top bar;
 *  - #3 rows grouped by project (collapsible), each group with a stacked bar and "53% approved";
 *  - #4 visible filter chips (Project / Discipline / Status / Responsible + More filters) with
 *    "N of 735"; Drawing Type spellings that differ only in case are one option;
 *  - #6 actions on the row (Log submission, Edit, ⋯), the status badge changes the status in one click,
 *    a row click opens the drawer. The whole-table "Edit" mode is gone.
 * Ungrouped ("Group: None") keeps the 50-rows-a-page pager of wave 1.
 * Wave 3:
 *  - #9 "Tidy the register" callout + wizard (TidyRegister.js);
 *  - #10 List | Matrix: the Matrix shows each project as floors × disciplines coloured by status (DrawingMatrix.js).
 * `readOnly` (BUG-002: Shop Drawings is View only for the role, or a Viewer account): no check boxes / bulk bar, a
 * plain status badge (StatusMenu without onSetStatus), no Log submission / Edit on the row (⋯ keeps the page's read
 * items: Open details, Copy link) and no Add drawing / Import in the empty register.
 */
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ExternalLink, FileSpreadsheet, FolderOpen, LayoutGrid, List, Pencil, Plus, Send, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ColumnsMenu, DataTable } from '@/components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterBar } from '@/components/ui/filter-bar';
import { RowActions } from '@/components/ui/row-actions';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { formatDate, formatNumber, toDateInputValue } from '@/lib/format';
import { TONE_TEXT_CLASS } from '@/lib/status';
import { cn } from '@/lib/utils';
import {
  BLOCKER_LABELS,
  GROUP_PAGE_SIZES,
  PAGE_SIZES,
  PIPELINE_GROUPS,
  STATUS_LABELS,
  anyAllFilter,
  GROUP_ROW_LIMIT,
  defaultOpenOnPage,
  disciplinesOf,
  displayPerson,
  drawingTitle,
  drawingTypeKey,
  labelize,
  mergedOptions,
  mostCommon,
  orderedGroups,
  paginate,
  projectNumber,
  projectSummary,
  realDrawingId,
  safeHref,
  sortWithColumns,
  statusNote,
  statusRank,
  updatedTime,
  valueKey,
} from './model';
import {
  DisciplineChip,
  DrawingIdText,
  GroupTitle,
  HoverActions,
  IconAction,
  MiniStackBar,
  PersonChip,
  ShowingText,
  SubLineExtras,
  SubLinePart,
  TableFoot,
  ViewSwitch,
  subClass,
} from './SdUi';
import { StatusMenu } from './StatusMenu';
import { DrawingMatrix } from './DrawingMatrix';
import { TidyCallout } from './TidyRegister';

const ALL_VIEWS = [
  { key: 'list', label: 'List', icon: List },
  { key: 'matrix', label: 'Matrix', icon: LayoutGrid },
];

const fmt = (value) => formatNumber(value, { maxDecimals: 0 });
const plural = (count, one, many = `${one}s`) => `${fmt(count)} ${count === 1 ? one : many}`;
const projectKeyOf = (drawing) => valueKey(drawing.project) || '__none__';
// Rows the open groups of a page show by default (3 projects × 4 rows, as in the 13 mockup).
const DEFAULT_OPEN_ROWS = 12;
// The row whose drawer is open: a navy edge on the left and a hover-tone background.
export const ACTIVE_ROW = '[&>td]:!bg-surface-2 [&>td:first-child]:shadow-[inset_3px_0_0_var(--brand)]';
const MENU_ITEM = 'h-[34px] gap-2.5 rounded-lg px-2.5 text-[13px] text-ink focus:bg-surface-hover focus:text-ink';

function Dash() {
  // `relative` keeps the absolutely positioned sr-only text inside the cell (on a phone the table scrolls sideways).
  return (
    <span className="relative">
      <span className="text-ink-4" aria-hidden="true">—</span>
      <span className="sr-only">None</span>
    </span>
  );
}

function DrawingCell({ drawing, showProject }) {
  const title = drawingTitle(drawing);
  const projectNo = projectNumber(drawing);
  const hasId = Boolean(realDrawingId(drawing.drawingId));
  return (
    <div className="min-w-0 py-0.5">
      <span className="block truncate text-[13px] font-semibold text-ink" title={`${title} · ${drawing.id}`}>{title}</span>
      {/* What gives way when the line is too long: the internal SD number, the revision and the type drop out (whole,
          from the end, all of them before the ID loses a character — SubLineExtras only gets leftover room), then the
          Drawing ID is cut at its start (its unique end stays). The project name + number stay, capped at the line minus
          3.5rem so the dot and the ID's last characters always fit on narrow columns (1366 and below); only next to a "No ID"
          tag (which never shrinks) is the project name cut, so the tag is never clipped on a narrow (phone) column. */}
      <span className="mt-0.5 flex min-w-0 items-center gap-1.5 whitespace-nowrap text-[11.5px] text-ink-3">
        {showProject && drawing.project ? (
          <>
            <span className={cn('min-w-0 truncate font-semibold text-ink-2', hasId ? 'max-w-[calc(100%-3.5rem)] shrink-0' : 'max-w-full shrink')} title={`${drawing.project}${projectNo ? ` #${projectNo}` : ''}`}>
              {drawing.project}{projectNo ? <span className="font-mono font-medium text-ink-3"> #{projectNo}</span> : null}
            </span>
            <span className="shrink-0" aria-hidden="true">·</span>
          </>
        ) : null}
        {/* Grouped (no project on the line) the ID keeps its full width; ungrouped it gives way to the project. */}
        <DrawingIdText value={drawing.drawingId} keepEnd className={showProject && drawing.project ? 'min-w-0 shrink' : 'min-w-0 max-w-full shrink-0'} />
        <SubLineExtras>
          {drawing.dtype ? <SubLinePart title={drawing.dtype}>{drawing.dtype}</SubLinePart> : null}
          {drawing.rev && drawing.rev !== '-' ? <SubLinePart className="font-mono">{drawing.rev}</SubLinePart> : null}
          <SubLinePart className="font-mono text-ink-4">{drawing.id}</SubLinePart>
        </SubLineExtras>
      </span>
    </div>
  );
}

function StatusCell({ drawing, onSetStatus, onReply }) {
  const note = statusNote(drawing);
  return (
    <div className="min-w-0" data-testid={`shop-drawing-status-${drawing.id}`}>
      <StatusMenu drawing={drawing} onSetStatus={onSetStatus} onReply={onReply} />
      {note ? (
        <span className={cn(subClass, 'truncate', note.tone === 'gray' ? 'text-ink-3' : TONE_TEXT_CLASS[note.tone])} title={note.text}>
          {note.text}
        </span>
      ) : null}
    </div>
  );
}

function UpdatedCell({ drawing }) {
  const time = updatedTime(drawing);
  if (!time) return <Dash />;
  const who = displayPerson(drawing.updby);
  return (
    <span className="block leading-tight" title={formatDate(new Date(time))}>
      <span className="text-[13px] font-semibold text-ink">{formatDate(new Date(time), { year: 'auto' })}</span>
      {who ? <span className={subClass}>{who}</span> : null}
    </span>
  );
}

function TextCell({ value, mono = false }) {
  const text = String(value ?? '').trim();
  if (!text || text === '-') return <Dash />;
  return <span className={cn('text-[12.5px] text-ink-2', mono && 'font-mono text-[11.5px]')} title={text}>{text}</span>;
}

function LinkCell({ href: raw, label }) {
  const href = safeHref(raw);
  if (!href) return String(raw || '').trim() ? <TextCell value={raw} /> : <Dash />;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand-ink hover:underline">
      {label} <ExternalLink className="h-3 w-3" aria-hidden="true" />
    </a>
  );
}

function GroupMenu({ grouped, onGroupedChange, onExpandAll, onCollapseAll }) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="gap-0.5 px-2 text-ink-2" data-testid="shop-drawings-group-menu">
          <span>Group: <b className="font-bold text-ink">{grouped ? 'Project' : 'None'}</b></span>
          <ChevronDown className="!size-3 text-ink-3" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px] rounded-xl border-line bg-surface p-1.5 text-ink shadow-overlay">
        <DropdownMenuLabel className="px-2.5 pb-1 pt-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Group rows by</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={grouped ? 'project' : 'none'} onValueChange={(value) => onGroupedChange(value === 'project')}>
          <DropdownMenuRadioItem value="project" className="h-[34px] rounded-lg text-[13px]">Project</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="none" className="h-[34px] rounded-lg text-[13px]">No grouping (pages of 50)</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        {grouped ? (
          <>
            <DropdownMenuSeparator className="my-1 bg-line-2" />
            <DropdownMenuLabel className="px-2.5 pb-1 pt-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">On this page</DropdownMenuLabel>
            <DropdownMenuItem className={MENU_ITEM} onSelect={onExpandAll}>Open every project</DropdownMenuItem>
            <DropdownMenuItem className={MENU_ITEM} onSelect={onCollapseAll}>Close every project</DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AllDrawingsTab({
  loading,
  drawings,
  rows,
  filters,
  onFiltersChange,
  onClearFilters,
  grouped,
  onGroupedChange,
  sort,
  onSortChange,
  page,
  pageSize,
  onPage,
  onPageSize,
  groupPageSize = GROUP_PAGE_SIZES[0],
  onGroupPageSize,
  selectedIds,
  onSelectionChange,
  onOpen,
  onEdit,
  onLogSubmission,
  onSetStatus,
  onReply,
  rowMenuItems,
  renderBulkBar,
  onAdd,
  onImport,
  activeId,
  projectHref,
  view = 'list',
  onViewChange,
  tidy,
  onReviewTidy,
  readOnly = false,
}) {
  const matrix = view === 'matrix';
  /* filter options — counted over the whole register, spellings merged */
  const options = useMemo(() => ({
    project: mergedOptions(drawings, (drawing) => drawing.project, { emptyLabel: 'No project' }),
    disc: mergedOptions(drawings, (drawing) => drawing.disc || 'OTHER', { keyOf: (value) => value.toUpperCase(), labelOf: (value) => value.toUpperCase() }),
    status: mergedOptions(drawings, (drawing) => drawing.status, { keyOf: (value) => value.toUpperCase(), labelOf: (value) => labelize(value.toUpperCase(), STATUS_LABELS) }),
    resp: mergedOptions(drawings, (drawing) => drawing.resp, { labelOf: displayPerson, emptyLabel: 'Unassigned' }),
    dtype: mergedOptions(drawings, (drawing) => drawing.dtype, { keyOf: drawingTypeKey, emptyLabel: 'No type' }),
    blocker: mergedOptions(drawings, (drawing) => drawing.blocker || 'NONE', { keyOf: (value) => value.toUpperCase(), labelOf: (value) => labelize(value.toUpperCase(), BLOCKER_LABELS) }),
    updby: mergedOptions(drawings, (drawing) => drawing.updby, { labelOf: displayPerson, emptyLabel: 'Nobody' }),
    stage: PIPELINE_GROUPS.map((group) => ({ value: group.key, label: group.short || group.label })),
  }), [drawings]);

  /* project facts for group rows — the whole project, whatever the filters */
  const projects = useMemo(() => {
    const map = new Map();
    drawings.forEach((drawing) => {
      const key = projectKeyOf(drawing);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(drawing);
    });
    return new Map([...map.entries()].map(([key, list]) => [key, {
      label: mostCommon(list, (drawing) => drawing.project) || 'No project',
      number: mostCommon(list, projectNumber),
      summary: projectSummary(list),
    }]));
  }, [drawings]);

  const columns = useMemo(() => [
    {
      key: 'drawing', header: 'Drawing', hideable: false, sortable: true, truncate: true, minWidth: 220,
      sortValue: (drawing) => drawingTitle(drawing).toLowerCase(),
      render: (drawing) => <DrawingCell drawing={drawing} showProject={!grouped} />,
    },
    { key: 'project', header: 'Project', defaultHidden: true, sortable: true, width: 170, truncate: true, sortValue: (drawing) => drawing.project, render: (drawing) => <TextCell value={drawing.project} /> },
    { key: 'disc', header: 'Disc.', title: 'Discipline', width: 76, sortable: true, render: (drawing) => <DisciplineChip disc={drawing.disc} /> },
    { key: 'block', header: 'Block / Zone', width: 136, sortable: true, truncate: true, render: (drawing) => <TextCell value={drawing.block} /> },
    { key: 'floor', header: 'Floor / Area', width: 170, defaultHidden: true, sortable: true, truncate: true, render: (drawing) => <TextCell value={drawing.floor} /> },
    {
      key: 'status', header: 'Status', width: 212, sortable: true, truncate: true,
      sortValue: (drawing) => statusRank(drawing.status),
      render: (drawing) => <StatusCell drawing={drawing} onSetStatus={readOnly ? null : onSetStatus} onReply={readOnly ? null : onReply} />,
    },
    { key: 'resp', header: 'Responsible', width: 140, sortable: true, truncate: true, sortValue: (drawing) => displayPerson(drawing.resp), render: (drawing) => <PersonChip name={drawing.resp} /> },
    { key: 'action', header: 'Action note', defaultHidden: true, width: 220, truncate: true, render: (drawing) => <TextCell value={drawing.action} /> },
    { key: 'target', header: 'Target date', defaultHidden: true, width: 120, sortable: true, sortValue: (drawing) => toDateInputValue(drawing.target) || '', render: (drawing) => (drawing.target ? <span className="text-[12.5px] text-ink-2">{formatDate(drawing.target)}</span> : <Dash />) },
    { key: 'pdf', header: 'PDF', defaultHidden: true, width: 72, render: (drawing) => <LinkCell href={drawing.pdf} label="PDF" /> },
    { key: 'dwg', header: 'DWG', defaultHidden: true, width: 72, render: (drawing) => <LinkCell href={drawing.dwg} label="DWG" /> },
    { key: 'notes', header: 'Notes', defaultHidden: true, width: 220, truncate: true, render: (drawing) => <TextCell value={drawing.notes} /> },
    { key: 'project_no', header: 'Project No.', defaultHidden: true, width: 104, sortable: true, render: (drawing) => <TextCell value={drawing.project_no} mono /> },
    {
      key: 'updated', header: 'Updated', width: 112, sortable: true, defaultSortDir: 'desc',
      sortValue: updatedTime,
      render: (drawing) => <UpdatedCell drawing={drawing} />,
    },
    {
      key: 'actions', header: '', width: 100, hideable: false,
      render: (drawing) => (
        <HoverActions>
          {readOnly ? null : (
            <>
              <IconAction icon={Send} label="Log submission" onClick={() => onLogSubmission(drawing)} data-testid={`row-log-submission-${drawing.id}`} />
              <IconAction icon={Pencil} label="Edit" onClick={() => onEdit(drawing)} data-testid={`row-edit-${drawing.id}`} />
            </>
          )}
          <RowActions label={`More actions for ${drawingTitle(drawing)}`} items={rowMenuItems(drawing)} size="sm" data-testid={`row-more-${drawing.id}`} />
        </HoverActions>
      ),
    },
  ], [grouped, onEdit, onLogSubmission, onReply, onSetStatus, readOnly, rowMenuItems]);

  const columnState = useColumnVisibility('shop-drawings.all', columns);
  const sortedRows = useMemo(() => sortWithColumns(rows, sort, columns), [rows, sort, columns]);
  const groupBy = useCallback((drawing) => ({ key: projectKeyOf(drawing), label: projects.get(projectKeyOf(drawing))?.label || drawing.project || 'No project' }), [projects]);
  const groups = useMemo(() => (grouped ? orderedGroups(sortedRows, groupBy) : []), [grouped, sortedRows, groupBy]);
  // 13 #5: grouped by project, the list pages by projects (5 a page by default), each open project showing its
  // first rows + "more in <project>"; ungrouped, it pages by drawings (50 a page).
  const groupPages = useMemo(() => paginate(groups, page, groupPageSize), [groups, page, groupPageSize]);
  const pageGroups = groupPages.rows;

  /* which project groups are open: the first ones of the page by default; all of them while filtering */
  const anyFilter = anyAllFilter(filters);
  const viewKey = JSON.stringify([filters, sort, groupPages.page, groupPageSize]);
  const [collapsedState, setCollapsedState] = useState({ key: null, value: [] });
  const collapsed = useMemo(() => {
    if (collapsedState.key === viewKey) return collapsedState.value;
    if (anyFilter) return [];
    const open = new Set(defaultOpenOnPage(pageGroups, { rowLimit: GROUP_ROW_LIMIT, budget: DEFAULT_OPEN_ROWS }));
    return pageGroups.filter((group) => !open.has(group.key)).map((group) => group.key);
  }, [collapsedState, viewKey, anyFilter, pageGroups]);
  const setCollapsed = useCallback((value) => setCollapsedState({ key: viewKey, value }), [viewKey]);

  const paged = useMemo(() => paginate(sortedRows, page, pageSize), [sortedRows, page, pageSize]);
  const tableRows = useMemo(() => (grouped ? pageGroups.flatMap((group) => group.rows) : paged.rows), [grouped, pageGroups, paged.rows]);
  const pageIds = useMemo(() => tableRows.map((drawing) => drawing.id), [tableRows]);
  const total = drawings.length;
  const sortLabel = !sort ? '' : sort.key === 'updated'
    ? (sort.dir === 'desc' ? 'latest update first' : 'oldest update first')
    : `sorted by ${columns.find((column) => column.key === sort.key)?.title || columns.find((column) => column.key === sort.key)?.header || sort.key}${sort.dir === 'desc' ? ' (descending)' : ''}`;

  const filterDefs = [
    { key: 'project', label: 'Project', options: options.project, value: filters.project, onChange: (project) => onFiltersChange({ project }), testId: 'all-filter-project', popoverClassName: 'w-72' },
    { key: 'disc', label: 'Discipline', options: options.disc, value: filters.disc, onChange: (disc) => onFiltersChange({ disc }), testId: 'all-filter-discipline' },
    { key: 'status', label: 'Status', options: options.status, value: filters.status, onChange: (status) => onFiltersChange({ status }), testId: 'all-filter-status' },
    { key: 'resp', label: 'Responsible', options: options.resp, value: filters.resp, onChange: (resp) => onFiltersChange({ resp }), testId: 'all-filter-responsible' },
    { key: 'dtype', label: 'Drawing type', options: options.dtype, value: filters.dtype, onChange: (dtype) => onFiltersChange({ dtype }), more: true, testId: 'all-filter-drawing-type' },
    { key: 'blocker', label: 'Blocker', options: options.blocker, value: filters.blocker, onChange: (blocker) => onFiltersChange({ blocker }), more: true, testId: 'all-filter-blocker' },
    { key: 'updby', label: 'Updated by', options: options.updby, value: filters.updby, onChange: (updby) => onFiltersChange({ updby }), more: true, testId: 'all-filter-updated-by' },
    { key: 'stage', label: 'Pipeline', options: options.stage, value: filters.stage || null, multiple: false, onChange: (stage) => onFiltersChange({ stage: stage || '' }), more: true, testId: 'pipeline-filter-chip' },
    // The Submission Log's "Submitted not logged" link: Submitted drawings that have no submission logged.
    { key: 'nolog', label: 'Submissions', valueLabel: 'none logged', type: 'toggle', value: Boolean(filters.nolog), onChange: (nolog) => onFiltersChange({ nolog: Boolean(nolog) }), more: true, testId: 'all-filter-no-submission' },
  ];

  const shownDrawings = pageGroups.reduce((sum, group) => sum + group.rows.length, 0);
  const footer = grouped ? (
    <TableFoot
      className="rounded-b-card"
      testId="shop-drawings-table-footer"
      left={loading ? null : (
        <span>
          {rows.length
            ? (
              <>
                Showing <b className="font-semibold text-ink-2">{groupPages.from === groupPages.to ? fmt(groupPages.from) : `${fmt(groupPages.from)}–${fmt(groupPages.to)}`}</b> of <b className="font-semibold text-ink-2">{fmt(groupPages.total)}</b> {groupPages.total === 1 ? 'project' : 'projects'}
                {' · '}<b className="font-semibold text-ink-2">{fmt(shownDrawings)}</b> {shownDrawings === 1 ? 'drawing' : 'drawings'}
                {groupPages.pageCount > 1 || shownDrawings !== rows.length ? <> of {fmt(rows.length)}</> : null}
                {sortLabel ? ` · ${sortLabel}` : ''}
              </>
            )
            : 'No drawings'}
        </span>
      )}
      page={groupPages.page}
      pageCount={groupPages.pageCount}
      onPage={groupPages.pageCount > 1 ? onPage : undefined}
      pageSize={groupPageSize}
      pageSizes={GROUP_PAGE_SIZES}
      onPageSize={onGroupPageSize && groups.length > GROUP_PAGE_SIZES[0] ? onGroupPageSize : undefined}
      pageSizeLabel="Projects"
      pageSizeAriaLabel="Projects per page"
    />
  ) : (
    <TableFoot
      className="rounded-b-card"
      testId="shop-drawings-table-footer"
      left={loading ? null : <ShowingText from={paged.from} to={paged.to} total={paged.total} noun={paged.total === 1 ? 'drawing' : 'drawings'} extra={sortLabel} />}
      page={paged.page}
      pageCount={paged.pageCount}
      onPage={onPage}
      pageSize={pageSize}
      pageSizes={PAGE_SIZES}
      onPageSize={onPageSize}
    />
  );

  return (
    <div className="space-y-3" data-testid="all-drawings-tab">
      {!loading && tidy && !readOnly ? <TidyCallout tidy={tidy} onReview={onReviewTidy} /> : null}
      <FilterBar
        testId="shop-drawings-filters"
        search={{
          value: filters.q,
          onChange: (q) => onFiltersChange({ q }),
          placeholder: 'Search drawings',
          label: 'Search drawings',
          testId: 'shop-drawings-search',
          className: 'sm:w-[184px]',
        }}
        filters={filterDefs}
        moreLabel="More"
        moreCount={false}
        onClearAll={onClearFilters}
        // While the register loads there is no count to give ("0 drawings" would be wrong for a moment).
        summary={loading ? (
          <span className="ui-skeleton-shimmer inline-block h-2.5 w-[72px] rounded-full align-middle" aria-hidden="true" data-testid="all-drawings-count-loading" />
        ) : (
          <span data-testid="all-drawings-count">
            {rows.length === total ? plural(total, 'drawing') : <><b>{fmt(rows.length)}</b> of {plural(total, 'drawing')}</>}
          </span>
        )}
        // One 36px toolbar line at 1440 (13 mockup): tight gaps, "Columns" without its count (the menu shows it).
        actions={(
          <span className="flex items-center gap-1">
            {!matrix ? (
              <>
                <GroupMenu
                  grouped={grouped}
                  onGroupedChange={onGroupedChange}
                  onExpandAll={() => setCollapsed([])}
                  onCollapseAll={() => setCollapsed(pageGroups.map((group) => group.key))}
                />
                <ColumnsMenu state={columnState} testId="shop-drawings-columns" showCount={false} className="gap-1 px-2" />
              </>
            ) : null}
            {onViewChange ? <ViewSwitch value={view} onChange={onViewChange} options={ALL_VIEWS} ariaLabel="Show drawings as" testId="all-view" compact /> : null}
          </span>
        )}
      />

      {matrix ? (
        <DrawingMatrix rows={rows} loading={loading} onOpen={onOpen} projectHref={projectHref} onClearFilters={onClearFilters} filtered={anyFilter} />
      ) : (
        <DataTable
          testId="shop-drawings-table"
          ariaLabel="Shop drawings"
          columns={columns}
          columnVisibility={columnState}
          rows={tableRows}
          rowKey="id"
          loading={loading}
          skeletonRows={10}
          sort={sort}
          onSortChange={onSortChange}
          manualSort
          selectable={!readOnly}
          selectedKeys={selectedIds}
          onSelectionChange={onSelectionChange}
          // Pages (of 50 drawings, or of 5 projects): a selection may span pages ("Select all N"); the page prunes
          // it to the filtered list.
          keepSelectionOnFilter
          getRowLabel={drawingTitle}
          onRowOpen={onOpen}
          rowTestId={(drawing) => `shop-drawing-row-${drawing.id}`}
          // The wave 1 check box ids (helper scripts and e2e still select drawings with them).
          checkboxTestId={(drawing) => `select-shop-drawing-${drawing.id}`}
          headerCheckboxTestId="select-all-shop-drawings"
          // Kit group row (13 mockup): 40px, "LUXOR TOWER · #69 · 30 drawings".
          groupRowSize="lg"
          groupCount={(group) => plural(group.rows.length, 'drawing')}
          rowClassName={activeId ? (drawing) => (drawing.id === activeId ? ACTIVE_ROW : undefined) : undefined}
          groupBy={grouped ? groupBy : undefined}
          collapsedGroups={grouped ? collapsed : undefined}
          onCollapsedGroupsChange={grouped ? setCollapsed : undefined}
          renderGroupHeader={(group) => {
            const facts = projects.get(group.key);
            return <GroupTitle label={facts?.label || group.label} number={facts?.number} noNumberText="no project no." discs={disciplinesOf(group.rows)} />;
          }}
          groupMeta={(group) => {
            const facts = projects.get(group.key);
            const summary = facts?.summary;
            if (!summary) return null;
            const href = group.key !== '__none__' ? projectHref?.(facts.label) : null;
            return (
              <>
                {href ? (
                  // Beside the title, not in it: the title is the group's collapse button.
                  <Link
                    to={href}
                    className="grid h-7 w-7 place-items-center rounded-lg text-ink-3 hover:bg-surface-hover hover:text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={`Open project ${facts.label}`}
                    title={`Open project ${facts.label}`}
                    data-testid={`shop-drawings-group-project-link`}
                  >
                    <FolderOpen className="h-[15px] w-[15px]" aria-hidden="true" />
                  </Link>
                ) : null}
                <MiniStackBar segments={summary.segments} label={`${summary.approved} of ${summary.total} approved`} className="hidden sm:flex" />
                <span className={cn('min-w-[92px] text-right text-[12.5px] font-bold tabular-nums', summary.approvedPct ? 'text-ink' : 'text-ink-3')}>
                  {summary.approvedPct}% approved
                </span>
              </>
            );
          }}
          initialRowCount={100}
          // A few rows per project so a page scans as projects; one project on its own (filtered) lists them all.
          groupRowLimit={pageGroups.length === 1 ? 50 : GROUP_ROW_LIMIT}
          groupMoreVariant="compact"
          rowCountStep={100}
          pagingKey={viewKey}
          footer={footer}
          empty={total ? (
            <EmptyState
              icon={FolderOpen}
              titleAs="p"
              title="No drawings match these filters"
              text="Try a different search, or clear the filters."
              action={<Button type="button" variant="outline" size="sm" onClick={onClearFilters}>Clear filters</Button>}
            />
          ) : (
            <EmptyState
              icon={FileSpreadsheet}
              titleAs="p"
              title="No shop drawings yet"
              text={readOnly ? 'Drawings show up here once they are added to the register.' : 'Add the first drawing, or import the register from Excel.'}
              action={readOnly ? null : (
                <>
                  <Button type="button" size="sm" onClick={onAdd}><Plus aria-hidden="true" /> Add drawing</Button>
                  <Button type="button" variant="outline" size="sm" onClick={onImport}><Upload aria-hidden="true" /> Import from Excel</Button>
                </>
              )}
            />
          )}
          // The bar offers "Select all N" once every drawing of this page is selected.
          renderBulkBar={renderBulkBar && !readOnly ? (args) => renderBulkBar({ ...args, pageIds }) : undefined}
        />
      )}
    </div>
  );
}
